/* eslint-disable react/prop-types */
/* eslint-disable react/no-array-index-key */
import React from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";

const RATING_COLORS = {
  VL: "#c8e6c9",
  L: "#388e3c",
  M: "#fdd835",
  H: "#ef6c00",
  E: "#c62828",
};

const RATING_TEXT = {
  VL: "#33691e",
  L: "#ffffff",
  M: "#5d4037",
  H: "#ffffff",
  E: "#ffffff",
};

const RATINGS = ["", "VL", "L", "M", "H", "E"];
const RATING_LABELS = { VL: "Very Low", L: "Low", M: "Medium", H: "High", E: "Extreme" };

export default function PrioritisationMatrixPanel({
  yLabel,
  columns = [],
  rows = [],
  cells = {},
  setCells,
}) {
  const getCell = (rowId, colId) => cells[`${rowId}:${colId}`] || { rating: "", color: "#ffffff" };

  const updateCell = (rowId, colId, rating) =>
    setCells((prev) => ({
      ...prev,
      [`${rowId}:${colId}`]: { rating, color: RATING_COLORS[rating] || "#ffffff" },
    }));

  if (!columns.length || !rows.length) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Add consequence levels in the Consequence Table tab and plausibility ratings in the
          Plausibility Table tab to populate this matrix.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ overflowX: "auto" }}>
        <Table
          sx={{
            tableLayout: "fixed",
            width: "100%",
            borderCollapse: "collapse",
            "& td, & th": {
              border: "1px solid #bdbdbd",
              padding: "6px 10px",
              verticalAlign: "middle",
            },
          }}
        >
          <colgroup>
            <col style={{ width: 220 }} />
            {columns.map((c) => (
              <col key={c.id} style={{ width: `${Math.floor(640 / columns.length)}px` }} />
            ))}
          </colgroup>

          <TableHead>
            <TableRow>
              <TableCell
                sx={{ bgcolor: "#ede7f6", color: "#52009b", fontWeight: 700, fontSize: 13 }}
              >
                {yLabel}
              </TableCell>
              {columns.map((col) => (
                <TableCell
                  key={col.id}
                  sx={{
                    bgcolor: "#ede7f6",
                    color: "#52009b",
                    fontWeight: 700,
                    fontSize: 13,
                    textAlign: "center",
                  }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell
                  sx={{ bgcolor: "#f5f5f5", fontWeight: 700, fontSize: 12, color: "#37474f" }}
                >
                  {row.rating}
                </TableCell>
                {columns.map((col) => {
                  const cell = getCell(row.id, col.id);
                  const bg = cell.rating ? RATING_COLORS[cell.rating] || "#fff" : "#fff";
                  const textColor = cell.rating ? RATING_TEXT[cell.rating] || "#333" : "#999";
                  return (
                    <TableCell
                      key={col.id}
                      sx={{ bgcolor: bg, p: "4px 8px !important", textAlign: "center" }}
                    >
                      <Select
                        value={cell.rating || ""}
                        onChange={(e) => updateCell(row.id, col.id, e.target.value)}
                        variant="standard"
                        displayEmpty
                        disableUnderline
                        sx={{
                          color: textColor,
                          fontWeight: 700,
                          fontSize: 14,
                          "& .MuiSelect-icon": { color: textColor },
                          minWidth: 48,
                        }}
                      >
                        {RATINGS.map((r) => (
                          <MenuItem
                            key={r}
                            value={r}
                            sx={{
                              bgcolor: r ? RATING_COLORS[r] : "inherit",
                              color: r ? RATING_TEXT[r] : "inherit",
                              fontWeight: 700,
                              "&:hover": { filter: "brightness(0.92)" },
                            }}
                          >
                            {r ? `${r} — ${RATING_LABELS[r]}` : "—"}
                          </MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      {/* Legend */}
      <Box display="flex" gap={1} mt={2} flexWrap="wrap" alignItems="center">
        <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
          Level of Residual Risk:
        </Typography>
        {Object.entries(RATING_LABELS).map(([key, label]) => (
          <Box
            key={key}
            sx={{
              bgcolor: RATING_COLORS[key],
              color: RATING_TEXT[key],
              px: 2,
              py: 0.5,
              borderRadius: 1,
              fontSize: 12,
              fontWeight: 700,
            }}
          >
            {label}
          </Box>
        ))}
      </Box>
    </Box>
  );
}
